import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { XMarkIcon, PaperClipIcon, ArrowUpTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { inventoryAPI } from '../../../services/inventoryApi';
import { uploadFile, openFileInNewTab } from '../../../services/fileApi';
import SearchableSelect from '../../Common/SearchableSelect';

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n) => `Rs ${r2(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

// same arithmetic as the server: qty × price, less discount, plus tax on the discounted amount
const lineTotals = (qty, price, discountPercent, taxPercent) => {
    const gross = r2((Number(qty) || 0) * (Number(price) || 0));
    const disc = r2(gross * (Number(discountPercent) || 0) / 100);
    const tax = r2((gross - disc) * (Number(taxPercent) || 0) / 100);
    return { gross, disc, tax, total: r2(gross - disc + tax) };
};

/**
 * Add / edit a supplier's quotation for an approved purchase request.
 * Lines come from the request; the user enters the quoted price (and optional discount / tax) per line.
 */
const QuotationForm = ({ isOpen, onClose, onSaved, purchaseRequest, quotation = null, quotedSupplierIds = [] }) => {
    const isEdit = !!quotation;
    const [suppliers, setSuppliers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(null);

    useEffect(() => {
        if (!isOpen || !purchaseRequest) return;
        const byLine = new Map((quotation?.lineItems || []).map(l => [String(l.prLineId), l]));
        setForm({
            supplier: quotation ? String(quotation.supplier?._id || quotation.supplier) : '',
            quotationDate: toDateInput(quotation?.quotationDate || new Date()),
            validUntil: toDateInput(quotation?.validUntil),
            supplierReference: quotation?.supplierReference || '',
            deliveryDays: quotation?.deliveryDays ?? '',
            paymentTerms: quotation?.paymentTerms || '',
            deliveryTerms: quotation?.deliveryTerms || '',
            notes: quotation?.notes || '',
            attachments: quotation?.attachments || [],
            lineItems: (purchaseRequest.lineItems || []).map(pl => {
                const q = byLine.get(String(pl._id));
                return {
                    prLineId: pl._id, lineNo: pl.lineNo, description: pl.itemDescription, uom: pl.uom, quantity: pl.quantityRequested,
                    estimatedUnitPrice: pl.estimatedUnitPrice || 0,
                    unitPrice: q ? q.unitPrice : '', discountPercent: q?.discountPercent || '', taxPercent: q?.taxPercent || '', remarks: q?.remarks || '',
                };
            }),
        });
        setErrors({});
        if (!isEdit) {
            inventoryAPI.getSuppliers({ limit: 1000, isActive: 'true', usableFor: purchaseRequest.procurementType === 'SERVICE' ? 'services' : 'goods' })
                .then(res => setSuppliers(res.data?.suppliers || res.data || []))
                .catch(err => toast.error(err.response?.data?.message || 'Failed to load suppliers'));
        }
    }, [isOpen, purchaseRequest, quotation, isEdit]);

    const totals = useMemo(() => {
        if (!form) return { subtotal: 0, discount: 0, tax: 0, grandTotal: 0 };
        return form.lineItems.reduce((t, l) => {
            const x = lineTotals(l.quantity, l.unitPrice, l.discountPercent, l.taxPercent);
            return { subtotal: r2(t.subtotal + x.gross), discount: r2(t.discount + x.disc), tax: r2(t.tax + x.tax), grandTotal: r2(t.grandTotal + x.total) };
        }, { subtotal: 0, discount: 0, tax: 0, grandTotal: 0 });
    }, [form]);

    if (!isOpen || !form) return null;

    const setField = (field, value) => { setForm(f => ({ ...f, [field]: value })); setErrors(e => ({ ...e, [field]: null })); };
    const setLine = (i, field, value) => {
        setForm(f => ({ ...f, lineItems: f.lineItems.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)) }));
        setErrors(e => ({ ...e, [`line_${i}`]: null }));
    };

    const supplierOptions = suppliers
        .filter(s => !quotedSupplierIds.includes(String(s._id)))
        .map(s => ({ value: String(s._id), label: `${s.name}${s.code ? ` (${s.code})` : ''}` }));

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                const res = await uploadFile(file, { category: 'Other', entityType: 'supplier_quotation', title: file.name });
                const d = res.data?.data;
                if (d?.fileId) {
                    setForm(f => ({ ...f, attachments: [...f.attachments, { fileId: d.fileId, name: d.originalName || file.name }] }));
                    toast.success(res.data?.message || `${file.name} uploaded`);
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to upload the document');
        } finally { setUploading(false); }
    };

    const validate = () => {
        const errs = {};
        if (!isEdit && !form.supplier) errs.supplier = 'Select the supplier';
        form.lineItems.forEach((l, i) => {
            if (l.unitPrice === '' || l.unitPrice == null || Number(l.unitPrice) < 0) errs[`line_${i}`] = 'Enter the quoted unit price';
        });
        if (form.validUntil && form.quotationDate && form.validUntil < form.quotationDate) errs.validUntil = 'Cannot be before the quotation date';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        const payload = {
            purchaseRequest: purchaseRequest._id,
            ...(!isEdit && { supplier: form.supplier }),
            quotationDate: form.quotationDate || null,
            validUntil: form.validUntil || null,
            supplierReference: form.supplierReference,
            deliveryDays: form.deliveryDays === '' ? null : Number(form.deliveryDays),
            paymentTerms: form.paymentTerms,
            deliveryTerms: form.deliveryTerms,
            notes: form.notes,
            attachments: form.attachments,
            lineItems: form.lineItems.map(l => ({
                prLineId: l.prLineId, unitPrice: Number(l.unitPrice),
                discountPercent: Number(l.discountPercent) || 0, taxPercent: Number(l.taxPercent) || 0, remarks: l.remarks,
            })),
        };
        try {
            const res = isEdit
                ? await inventoryAPI.updateSupplierQuotation(quotation._id, payload)
                : await inventoryAPI.createSupplierQuotation(payload);
            toast.success(res.message || 'Quotation saved');
            onSaved?.(res.data);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save the quotation');
        } finally { setSaving(false); }
    };

    const input = 'w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500';
    const label = 'block text-xs font-medium text-gray-600 mb-1';

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg">
                    <h3 className="text-base font-semibold text-white">
                        {isEdit ? `Edit Quotation ${quotation.quotationNumber}` : 'Add Supplier Quotation'}
                        <span className="ml-2 text-xs font-normal text-indigo-100">for {purchaseRequest.prNumber}</span>
                    </h3>
                    <button onClick={onClose} className="text-white/80 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
                </div>

                <div className="overflow-y-auto flex-1 p-4 space-y-4">
                    {/* Header */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="md:col-span-2">
                            <label className={label}>Supplier <span className="text-red-500">*</span></label>
                            {isEdit ? (
                                <div className="px-2 py-1.5 text-xs border border-gray-200 rounded bg-gray-50 text-gray-700">{quotation.supplier?.name || '—'}</div>
                            ) : (
                                <SearchableSelect
                                    value={form.supplier}
                                    onChange={(e) => setField('supplier', e.target.value)}
                                    options={supplierOptions}
                                    placeholder={suppliers.length ? 'Select supplier…' : 'Loading suppliers…'}
                                    error={errors.supplier || ''}
                                    size="sm"
                                />
                            )}
                            {errors.supplier && <p className="text-xs text-red-500 mt-0.5">{errors.supplier}</p>}
                        </div>
                        <div>
                            <label className={label}>Supplier's Quote Ref.</label>
                            <input className={input} value={form.supplierReference} onChange={e => setField('supplierReference', e.target.value)} placeholder="e.g. Q-2026-118" />
                        </div>
                        <div>
                            <label className={label}>Delivery (days)</label>
                            <input type="number" min="0" className={input} value={form.deliveryDays} onChange={e => setField('deliveryDays', e.target.value)} placeholder="e.g. 7" />
                        </div>
                        <div>
                            <label className={label}>Quotation Date</label>
                            <input type="date" className={input} value={form.quotationDate} onChange={e => setField('quotationDate', e.target.value)} />
                        </div>
                        <div>
                            <label className={label}>Valid Until</label>
                            <input type="date" className={input} value={form.validUntil} onChange={e => setField('validUntil', e.target.value)} />
                            {errors.validUntil && <p className="text-xs text-red-500 mt-0.5">{errors.validUntil}</p>}
                        </div>
                        <div>
                            <label className={label}>Payment Terms</label>
                            <input className={input} value={form.paymentTerms} onChange={e => setField('paymentTerms', e.target.value)} placeholder="e.g. 30 days credit" />
                        </div>
                        <div>
                            <label className={label}>Delivery Terms</label>
                            <input className={input} value={form.deliveryTerms} onChange={e => setField('deliveryTerms', e.target.value)} placeholder="e.g. Delivered to store" />
                        </div>
                    </div>

                    {/* Lines */}
                    <div className="border border-gray-200 rounded-lg overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-gray-50 text-gray-500 uppercase">
                                <tr>
                                    <th className="px-2 py-2 text-left">#</th>
                                    <th className="px-2 py-2 text-left">Description</th>
                                    <th className="px-2 py-2 text-right">Qty</th>
                                    <th className="px-2 py-2 text-right">Unit Price *</th>
                                    <th className="px-2 py-2 text-right">Disc. %</th>
                                    <th className="px-2 py-2 text-right">Tax %</th>
                                    <th className="px-2 py-2 text-right">Line Total</th>
                                    <th className="px-2 py-2 text-left">Remarks</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {form.lineItems.map((l, i) => {
                                    const t = lineTotals(l.quantity, l.unitPrice, l.discountPercent, l.taxPercent);
                                    return (
                                        <tr key={String(l.prLineId)}>
                                            <td className="px-2 py-1.5 text-gray-700">{l.lineNo}</td>
                                            <td className="px-2 py-1.5 text-gray-900">
                                                {l.description}
                                                <div className="text-[10px] text-gray-400">Estimate: {money(l.estimatedUnitPrice)} / unit</div>
                                            </td>
                                            <td className="px-2 py-1.5 text-right whitespace-nowrap">{l.quantity} {l.uom}</td>
                                            <td className="px-2 py-1.5 w-32">
                                                <input type="number" min="0" step="0.01" aria-label={`Unit price line ${l.lineNo}`}
                                                    className={`${input} text-right ${errors[`line_${i}`] ? 'border-red-400' : ''}`}
                                                    value={l.unitPrice} onChange={e => setLine(i, 'unitPrice', e.target.value)} placeholder="0.00" />
                                                {errors[`line_${i}`] && <p className="text-[10px] text-red-500 mt-0.5">{errors[`line_${i}`]}</p>}
                                            </td>
                                            <td className="px-2 py-1.5 w-20"><input type="number" min="0" max="100" step="0.01" aria-label={`Discount % line ${l.lineNo}`} className={`${input} text-right`} value={l.discountPercent} onChange={e => setLine(i, 'discountPercent', e.target.value)} placeholder="0" /></td>
                                            <td className="px-2 py-1.5 w-20"><input type="number" min="0" max="100" step="0.01" aria-label={`Tax % line ${l.lineNo}`} className={`${input} text-right`} value={l.taxPercent} onChange={e => setLine(i, 'taxPercent', e.target.value)} placeholder="0" /></td>
                                            <td className="px-2 py-1.5 text-right font-medium text-gray-900 whitespace-nowrap">{money(t.total)}</td>
                                            <td className="px-2 py-1.5 w-40"><input className={input} value={l.remarks} onChange={e => setLine(i, 'remarks', e.target.value)} placeholder="Brand, warranty…" /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex flex-col md:flex-row gap-4">
                        {/* Notes + attachments */}
                        <div className="flex-1 space-y-3">
                            <div>
                                <label className={label}>Notes</label>
                                <textarea rows={2} className={`${input} resize-none`} value={form.notes} onChange={e => setField('notes', e.target.value)} placeholder="Warranty, conditions…" />
                            </div>
                            <div>
                                <label className={label}>Supplier's Quotation Document</label>
                                <div className="flex flex-wrap items-center gap-2">
                                    {form.attachments.map((a, i) => (
                                        <span key={a.fileId || i} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded">
                                            <PaperClipIcon className="w-3.5 h-3.5" />
                                            <button type="button" className="hover:underline" onClick={() => openFileInNewTab(a.fileId).catch(() => toast.error('Could not open the document'))}>{a.name}</button>
                                            <button type="button" title="Remove" onClick={() => setField('attachments', form.attachments.filter((_, idx) => idx !== i))}><TrashIcon className="w-3.5 h-3.5 text-red-500" /></button>
                                        </span>
                                    ))}
                                    <label className={`inline-flex items-center gap-1 px-2 py-1 text-xs border border-dashed border-gray-300 rounded cursor-pointer hover:bg-gray-50 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                                        <ArrowUpTrayIcon className="w-3.5 h-3.5" /> {uploading ? 'Uploading…' : 'Attach PDF / image'}
                                        <input type="file" multiple accept=".pdf,image/*" className="hidden" onChange={handleUpload} />
                                    </label>
                                </div>
                            </div>
                        </div>
                        {/* Totals */}
                        <div className="w-full md:w-64 border border-gray-200 rounded-lg overflow-hidden text-xs self-start">
                            <div className="flex justify-between px-3 py-1.5"><span className="text-gray-500">Subtotal</span><span>{money(totals.subtotal)}</span></div>
                            <div className="flex justify-between px-3 py-1.5"><span className="text-gray-500">Discount</span><span>− {money(totals.discount)}</span></div>
                            <div className="flex justify-between px-3 py-1.5"><span className="text-gray-500">Tax</span><span>{money(totals.tax)}</span></div>
                            <div className="flex justify-between px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold">
                                <span>Quotation Total</span><span data-testid="quotation-total">{money(totals.grandTotal)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                    <button onClick={handleSave} disabled={saving || uploading} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                        {saving ? 'Saving…' : isEdit ? 'Update Quotation' : 'Save Quotation'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default QuotationForm;
